/** Campos base de episodios cuyo valor está en minutos. */
export const DURATION_FIELD_BASES = ['duracion_min', 'total_min', 'max_min'];

export const DURATION_DISPLAY_FORMAT_OPTIONS = [
  { value: 'auto', label: 'Automático (min / h m / d h m)' },
  { value: 'minutes', label: 'Minutos (107 min)' },
  { value: 'hm', label: 'Horas compactas (1h 47m)' },
  { value: 'dhms', label: 'Días compactos (2d 3h 25m)' },
  { value: 'verbose', label: 'Texto completo (1 hora 47 minutos)' },
];

export const DURATION_TOOLTIP_FORMAT_OPTIONS = [
  { value: 'same', label: 'Igual que la visualización' },
  { value: 'verbose', label: 'Texto completo (1 hora 25 minutos)' },
  { value: 'minutes', label: 'Minutos (107 min)' },
  { value: 'hm', label: 'Compacto (1h 47m)' },
  { value: 'dhms', label: 'Con días (2d 3h 25m)' },
];

/** Normaliza minutos a entero >= 0. */
export function normalizeDurationMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
}

/** Descompone minutos en días, horas y minutos. */
export function splitDurationMinutes(totalMin) {
  const total = normalizeDurationMinutes(totalMin);
  if (total == null) return null;
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  return { total, days, hours, mins };
}

function compactParts({ days, hours, mins }, { alwaysShowMinutes = true } = {}) {
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins || (alwaysShowMinutes && !days && !hours)) parts.push(`${mins}m`);
  return parts.join(' ');
}

function verboseParts({ days, hours, mins }) {
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  if (mins || (!days && !hours)) parts.push(`${mins} ${mins === 1 ? 'minuto' : 'minutos'}`);
  return parts.join(' ');
}

/** Elige formato compacto según magnitud cuando format === 'auto'. */
export function autoDurationDisplayFormat(totalMin) {
  const parts = splitDurationMinutes(totalMin);
  if (!parts) return 'minutes';
  if (parts.total < 60) return 'minutes';
  if (parts.total < 1440) return 'hm';
  return 'dhms';
}

/**
 * Formatea minutos según el modo elegido.
 * @param {number|string} value - minutos
 * @param {string} format - auto | minutes | hm | dhms | verbose
 */
export function formatDurationMinutes(value, format = 'auto', { style } = {}) {
  const parts = splitDurationMinutes(value);
  if (!parts) return value != null && value !== '' ? String(value) : '';
  const mode = format === 'auto' ? autoDurationDisplayFormat(parts.total) : format;
  if (mode === 'minutes') return `${parts.total} min`;
  if (mode === 'verbose') return verboseParts(parts);
  return compactParts(parts);
}

export function isDurationFieldBase(base) {
  return DURATION_FIELD_BASES.includes(base);
}

/** Formato de visualización para un campo base de episodio. */
export function resolveFieldDurationFormat(episodeAnalysis, fieldBase) {
  const cfg = episodeAnalysis || {};
  const perCol = cfg.columnDurationFormats?.[fieldBase];
  if (perCol && perCol !== 'auto') return perCol;
  if (cfg.durationDisplayFormat && cfg.durationDisplayFormat !== 'auto') return cfg.durationDisplayFormat;
  return 'auto';
}

/** Formato de tooltip (hover) para duraciones. */
export function resolveTooltipDurationFormat(episodeAnalysis, fieldBase) {
  const cfg = episodeAnalysis || {};
  const tooltip = cfg.durationTooltipFormat || 'verbose';
  if (tooltip === 'same') return resolveFieldDurationFormat(cfg, fieldBase);
  return tooltip;
}

/** Formatea un valor de celda/eje si el campo base es duración. */
export function formatEpisodeDurationValue(value, episodeAnalysis, fieldBase, { tooltip = false } = {}) {
  if (!isDurationFieldBase(fieldBase)) {
    return value != null && value !== '' ? String(value) : '';
  }
  const fmt = tooltip
    ? resolveTooltipDurationFormat(episodeAnalysis, fieldBase)
    : resolveFieldDurationFormat(episodeAnalysis, fieldBase);
  return formatDurationMinutes(value, fmt, { style: tooltip ? 'tooltip' : 'display' });
}

/**
 * Construye metadatos de duración para gráficos/tablas a partir del spec de episodios.
 * @param {object} episodeAnalysis
 * @param {Array<{base,value,duration?}>} outputFields - de episodeOutputFields()
 * @param {string} yColumn - nombre final del eje Y
 */
export function buildYDurationChartConfig(episodeAnalysis, outputFields, yColumn) {
  if (!episodeAnalysis?.enabled || episodeAnalysis.chartSource === 'original' || !yColumn) {
    return null;
  }
  const field = (outputFields || []).find((f) => f.value === yColumn || f.base === yColumn);
  if (!field?.duration && !isDurationFieldBase(field?.base)) return null;
  const base = field.base;
  return {
    yColumnIsDuration: true,
    yDurationFormat: resolveFieldDurationFormat(episodeAnalysis, base),
    yDurationTooltipFormat: resolveTooltipDurationFormat(episodeAnalysis, base),
    durationFieldBase: base,
  };
}

/** Mapa { [columnaFinal]: formato } para DataTable. */
export function buildTableDurationFormats(episodeAnalysis, outputFields) {
  if (!episodeAnalysis?.enabled || episodeAnalysis.chartSource === 'original') return undefined;
  const map = {};
  for (const f of outputFields || []) {
    if (f.duration || isDurationFieldBase(f.base)) {
      map[f.value] = resolveFieldDurationFormat(episodeAnalysis, f.base);
    }
  }
  return Object.keys(map).length ? map : undefined;
}

/** Mapa { [columnaFinal]: formatoTooltip } para title/hover en tablas. */
export function buildTableDurationTooltipFormats(episodeAnalysis, outputFields) {
  if (!episodeAnalysis?.enabled || episodeAnalysis.chartSource === 'original') return undefined;
  const map = {};
  for (const f of outputFields || []) {
    if (f.duration || isDurationFieldBase(f.base)) {
      map[f.value] = resolveTooltipDurationFormat(episodeAnalysis, f.base);
    }
  }
  return Object.keys(map).length ? map : undefined;
}

export function formatTableDurationValue(value, column, columnDurationFormats, columnDurationTooltipFormats) {
  const displayFmt = columnDurationFormats?.[column];
  if (!displayFmt) return null;
  const n = normalizeDurationMinutes(value);
  if (n == null) return null;
  return formatDurationMinutes(n, displayFmt);
}

export function formatTableDurationTooltip(value, column, columnDurationFormats, columnDurationTooltipFormats) {
  const tooltipFmt = columnDurationTooltipFormats?.[column] || 'verbose';
  const n = normalizeDurationMinutes(value);
  if (n == null) return null;
  return formatDurationMinutes(n, tooltipFmt, { style: 'tooltip' });
}
